import React from "react";
import { useState } from "react";
import NAImage from "../assets/images/NotApplicable.svg";
import NoPainImage from "../assets/images/NoPainImage.svg";
import MildPainImage from "../assets/images/MildPainImage.svg";
import ModeratePainImage from "../assets/images/ModeratePainImage.svg";
import SeverePainImage from "../assets/images/SeverePainImage.svg";
import { readProfileImage } from "../utility/profileImage";
import useStore from "../store/store";

const imageMap = {
  "Not Applicable": NAImage,
  "No Pain": NoPainImage,
  Mild: MildPainImage,
  Moderate: ModeratePainImage,
  Severe: SeverePainImage,
};

const PatientProfile = ({
  patient,
  patientImage,
  setPatientImage,
  clinicianNotes,
  setClinicianNotes,
  selectedTreatmentStatus,
  setSelectedTreatmentStatus,
  saveChanges,
  setSaveChanges,
}) => {
  const [newNote, setNewNote] = useState();
  const [imageError, setImageError] = useState("");
  const setPatient = useStore((state) => state.setPatient);
  const role = localStorage.getItem("role");
  const calculateAge = (dobString) => {
    const dob = new Date(dobString);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const isBirthdayPassed =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
    if (!isBirthdayPassed) age -= 1;
    return age;
  };

  const textFormat = (text) =>
    text.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

  const addImage = async (e) => {
    const file = e.target.files[0];
    try {
      const image = await readProfileImage(file);
      if (image) {
        setPatientImage(image);
        setSaveChanges(true);
        setImageError("");
      }
    } catch (error) {
      setImageError(error.message);
      e.target.value = "";
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const today = new Date().toISOString().split("T")[0]; // e.g., "2025-03-26"
      setClinicianNotes((prev) => ({
        ...prev,
        [today]: [...(prev[today] || []), newNote], // Append to today's array
      }));
      //setClinicianNotes((prev) => [...prev, newNote]);
      console.log("Pressed enter");
      setNewNote("");
      setSaveChanges(true);
    }
  };

  const changeTreatmentStatus = (e) => {
    setSelectedTreatmentStatus(e.target.value);
    setSaveChanges(true);
  };

  const updatePatient = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/patients/uid/${patient.uid}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            patientImage,
            treatmentStatus: selectedTreatmentStatus || "On treatment",
            clinicianNotes,
          }),
        }
      );
      if (response.ok) {
        const updatedPatient = await response.json();
        setSaveChanges(false);
        setPatientImage(updatedPatient.patientImage);
        setPatient(updatedPatient);

        console.log(
          "Patient updated successfully ",
          updatedPatient.patientImage
        );
      } else {
        console.log("Failed to update the patient ", response.statusText);
      }
    } catch (error) {
      console.error("Error updating patient ", error);
    }
  };

  return (
    <div style={{ padding: "1rem" }}>
      <div className="row wrapRow">
        <div className="patientCard">
          <div className="row">
            <div>
              {patientImage ? (
                <img
                  src={patientImage}
                  alt="profile"
                  style={{
                    margin: "0 1rem 1rem 0",
                    width: "5.5rem",
                    height: "5.5rem",
                    borderRadius: "0.5rem",
                    objectFit: "cover",
                  }}
                  onError={() =>
                    console.log("Image failed to load:", patientImage)
                  }
                />
              ) : null}
              {!patientImage && (
                <div>
                  <label htmlFor="patientImage" className="addImage">
                    +
                  </label>
                  <input
                    type="file"
                    name="patientImage"
                    id="patientImage"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={addImage}
                  />
                  {imageError && <div className="b3 error">{imageError}</div>}
                </div>
              )}
            </div>
            <div style={{ lineHeight: "1rem" }}>
              <div className="h2">{patient.firstName}</div>
              <div className="h4 midGray">patient</div>
              <div className="h4 midGray">{patient.uid}</div>
            </div>
          </div>
          <div className="grid patientGrid">
            <div className="h4 midGray">Sex</div>
            <div className="h4 black">{patient.gender}</div>
            <div className="h4 midGray">Age</div>
            <div className="h4 black">{calculateAge(patient.dateOfBirth)}</div>
            <div className="h4 midGray">Languages Known</div>
            <div className="h4 black">
              {patient.languages.map(String).join(",")}
            </div>
            <div className="h4 midGray">Primary Language</div>
            <div className="h4 black">{patient.primaryLanguage}</div>
            <div className="h4 midGray">Patient ID</div>
            <div className="h4 black">{patient.uid}</div>
            <div className="h4 midGray">Phone</div>
            <div className="h4 black">{patient.phoneNumber}</div>
            <div className="h4 midGray">Email</div>
            <div className="h4 black">{patient.email}</div>
          </div>
        </div>
        <div className="patientCard">
          <div className="h2">Clinical Impressions</div>
          <br />
          <div className="h4 midGray">{patient.clinicalImpressions}</div>
        </div>
        <div className="patientCard">
          <div className="h2">Diagnosis</div>
          <div className="h4" style={{ padding: "0.25rem 0" }}>
            Osteoarthritis
          </div>
          <div className="grid patientGrid">
            {Object.keys(patient.diagnosis.osteoarthritis).map((painArea) => (
              <React.Fragment key={painArea}>
                <div className="b2 midGray">{textFormat(painArea)}</div>
                <div className="b2 black">
                  {patient.diagnosis.osteoarthritis[painArea]}
                </div>
              </React.Fragment>
            ))}
          </div>
          <br />
          <div className="col" style={{ justifyContent: "space-around" }}>
            <div className="h4" style={{ padding: "0.25rem 0" }}>
              Arthritis
            </div>
            {patient.diagnosis?.arthritis &&
              Object.keys(patient.diagnosis.arthritis).map(
                (painArea) =>
                  patient.diagnosis.arthritis[painArea] && (
                    <div className="b2 midGray" key={painArea}>
                      {textFormat(painArea)}
                    </div>
                  )
              )}
            <div className="b2">
              <span className="midGray">Additional Diagnosis</span>
              <span className="black">
                &nbsp;&nbsp;{patient.diagnosis.additionalDiagnosis}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="treatmentRadio">
          <input
            type="radio"
            name="treatmentStatus"
            id="on treatment"
            value="On treatment"
            checked={selectedTreatmentStatus === "On treatment"}
            onChange={changeTreatmentStatus}
            disabled={role !== "Clinician"}
          />
          <label
            htmlFor="on treatment"
            className="b3"
            style={{ cursor: role === "Clinician" ? "pointer" : "default" }}
          >
            On treatment
          </label>
          <input
            type="radio"
            name="treatmentStatus"
            id="recovered"
            value="Recovered"
            checked={selectedTreatmentStatus === "Recovered"}
            onChange={changeTreatmentStatus}
            disabled={role !== "Clinician"}
          />
          <label
            htmlFor="recovered"
            className="b3"
            style={{ cursor: role === "Clinician" ? "pointer" : "default" }}
          >
            Recovered
          </label>
        </div>
      </div>
      <div className="patientCard" style={{ height: "fit-content" }}>
        <div className="h2 black">
          How often does the patient experience knee pain?
        </div>
        <div className="h4 midGray">
          Over the last 6 months, how often has the patient experienced joint
          pain?
        </div>
        <div className="painIndex b3">{patient.painFrequencyinLast6months}</div>
        <div className="h4 midGray">
          On average, how severe has the pain been?
        </div>
        <div
          className="grid"
          style={{
            margin: "1.125rem 0",
            gridTemplateColumns: "1fr 1fr",
            columnGap: "2.5rem",
            rowGap: "1.8rem",
          }}
        >
          {Object.keys(patient.painSeverityIn6months).map((key) => (
            <div className="col" key={key}>
              <div className="h3 textGrey">{textFormat(key)}</div>
              <div className="row centerAlign" style={{ marginTop: "0.5rem" }}>
                <img
                  style={{ marginRight: "1rem" }}
                  src={imageMap[patient.painSeverityIn6months[key]]}
                  alt={patient.painSeverityIn6months[key]}
                />
                <div className="b3">{patient.painSeverityIn6months[key]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {role === "Clinician" && (
        <>
          <div>
            <div className="h2">Clinician notes</div>
            <div className="row" style={{ marginTop: "1rem" }}>
              {Object.entries(clinicianNotes).map(([date, notes]) => (
                <div key={date} className="patientCard">
                  <div className="h2">Date: {date}</div>
                  <div className="h4 midGray" style={{ marginTop: "0.6rem" }}>
                    <ul>
                      {notes &&
                        notes.map((note, index) => <li key={index}>{note}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
              <div className="patientCard">
                <div className="h2">Add new note</div>
                <div className="h4 midGray" style={{ marginTop: "0.6rem" }}>
                  <textarea
                    className="patientFormInput"
                    value={newNote}
                    name="newClinicalNotes"
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ width: "100%" }}
                    placeholder=" Type and press enter ..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="textCenter">
            <button
              className="button1 h3"
              disabled={!saveChanges}
              onClick={updatePatient}
            >
              SAVE CHANGES
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PatientProfile;
